import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Creative, DisplayCreative } from '@feathr/blackbox';
import { ADS_CREATIVE_CLASSES } from '@feathr/blackbox';
import { EmptyState, Table } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import AddCreativeButton from '@feathr/extender/components/Creatives/AddCreativeButton';
import { AdDimensionsSelect } from '@feathr/extender/components/Creatives/AdDimensionsSelect';
import CreativeModal from '@feathr/extender/components/Creatives/CreativeModal';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import ProjectChannelSelect from './ProjectChannelSelect';
import ProjectCreativesColumns from './ProjectCreativesColumns';
import { useSelectedChannel } from './ProjectCreativesPage.hooks';
import type { ICreativesFilters } from './ProjectCreativesPage.types';
import ProjectNameSearch from './ProjectNameSearch';

import * as styles from './ProjectCreativesPage.css';

function ProjectCreativesPage(): JSX.Element {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();

  const [filters, setFilters] = useState<ICreativesFilters>({});
  const [editingCreative, setEditingCreative] = useState<DisplayCreative | null>(null);

  const { Campaigns, Creatives } = useStore();
  const localUrl = useLocalUrl();

  function handleEditCreative(creative: Creative): void {
    setEditingCreative(creative as DisplayCreative);
  }

  function handleCloseEditModal(): void {
    setEditingCreative(null);
  }

  const creativesResponse = Creatives.list({
    filters: {
      _cls__in: ADS_CREATIVE_CLASSES,
      event: eventId,
    },
  });

  const { selectedChannel, handleDimensionsChange, handleDimensionsClear } = useSelectedChannel({
    filters,
    setFilters,
  });

  const filterElements = [
    <React.Fragment key={'channel-filter'}>
      <ProjectChannelSelect filters={filters} setFilters={setFilters} />
      <AdDimensionsSelect
        channel={selectedChannel}
        isClearable={true}
        isSearchable={false}
        onChange={handleDimensionsChange}
        onClear={handleDimensionsClear}
        value={filters.spec}
        wrapperClassName={styles.dimensionsFilter}
      />
    </React.Fragment>,
    <ProjectNameSearch filters={filters} key={'name-search'} setFilters={setFilters} />,
  ];

  // TODO: Hook up add creative flow properly 7388
  const actions = <AddCreativeButton eventId={eventId} />;

  const isEmpty = creativesResponse.isLoaded && creativesResponse.models.length === 0;

  return (
    <Page
      actions={actions}
      contentClassName={isEmpty ? styles.fullHeightContent : undefined}
      title={t('Creatives')}
    >
      {isEmpty ? (
        <EmptyState
          description={t('Get started by adding a new creative to this project.')}
          height={'full'}
          label={t('No creatives')}
          theme={'white'}
          width={'full'}
        >
          <AddCreativeButton eventId={eventId} type={'secondary'} />
        </EmptyState>
      ) : (
        <Table<Creative>
          collection={Creatives}
          columns={ProjectCreativesColumns({
            Campaigns,
            localUrl,
            onEditCreative: handleEditCreative,
          })}
          filterElements={filterElements}
          filters={{
            ...filters,
            _cls__in: filters._cls__in ?? ADS_CREATIVE_CLASSES,
            event: eventId,
          }}
          initialSort={[{ id: 'name' }]}
        />
      )}
      {editingCreative && (
        <CreativeModal
          creative={editingCreative}
          onClose={handleCloseEditModal}
          opened={!!editingCreative}
        />
      )}
    </Page>
  );
}

export default observer(ProjectCreativesPage);
